import type {ColorData} from "../_types/ColorData";
import type {ColorWithStatistics} from "../_types/ColorWithStatistics";
import { Color, Lch, lch, rgb, differenceEuclidean } from "./customColor";
import {getColorWeight} from "./getColorWeight";

const MAX_ITERATIONS = 16;
const CONVERGENCE_THRESHOLD = 0.2;

const getColorDistance = differenceEuclidean;

export const getColorPaletteFromColorData = (
    colorData: ColorData,
    maxColors: number
): Array<ColorWithStatistics> => {

    const lchColors = colorData.uniqueColors
        .map(c => ({ ...lch(c), frequency: c.frequency, weight: c.weight }));

    let centerDifference = Number.MAX_VALUE;
    let centers = initializeCenters(lchColors, maxColors);

    const assignments = Array.from(lchColors, () => 0);

    for (let i = 0; i < MAX_ITERATIONS; i++) {

        for (let j = 0; j < lchColors.length; j++) {
            let minDistance = Number.MAX_VALUE;
            for (let k = 0; k < centers.length; k++) {
                const distance = getColorDistance(lchColors[j], centers[k]);
                if (distance < minDistance) {
                    assignments[j] = k;
                    minDistance = distance;
                }
            }
        }

        const newCenters = calculateCenters(lchColors, assignments, maxColors);
        const newCenterDifference = calculateCenterDifference(centers, newCenters);

        centers = newCenters;

        if (Math.abs(newCenterDifference - centerDifference) < CONVERGENCE_THRESHOLD) {
            break;
        }

        centerDifference = newCenterDifference;
    }

    return centers
        .map(c => ({
            ...rgb(c),
            weight: c.weight,
            frequency: c.frequency
        }));
};

const initializeCenters = (
    colors: Array<ColorWithStatistics>,
    size: number
): Array<ColorWithStatistics> => {

    const usedIndices = new Set<number>();
    const centers: Array<ColorWithStatistics> = [];
    const n = colors.length;

    const firstIndex = Math.floor(Math.random() * n);
    centers.push(colors[firstIndex]);
    usedIndices.add(firstIndex);

    for (let i = 1; i < size; i++) {

        let totalDistance = 0;
        const distances = Array.from({ length: n }, () => 0);

        for (let j = 0; j < n; j++) {

            let minDistance = Number.MAX_VALUE;

            for (const center of centers) {
                const distance = getColorDistance(colors[j], center);
                minDistance = Math.min(minDistance, distance);
            }

            distances[j] = minDistance;
            totalDistance += minDistance * colors[j].weight;
        }

        let sum = 0;
        let targetDistance = Math.random() * totalDistance;

        for (let j = 0; j < n; j++) {
            if (usedIndices.has(j)) continue; // Skip already used colors

            sum += distances[j] * colors[j].weight;

            if (sum > targetDistance) {
                centers.push(colors[j]);
                usedIndices.add(j);
                break;
            }
        }
    }

    return centers;
};

type LchWithStatistics = Lch & { weight: number; frequency: number };

const calculateCenters = (
    colors: Array<ColorWithStatistics>,
    assignments: Array<number>,
    maxColors: number
): Array<ColorWithStatistics> => {

    const sums: Array<LchWithStatistics> = Array.from(
        { length: maxColors },
        () => ({ mode: "lch", l: 0, c: 0, h: 0, frequency: 0, weight: 0 })
    );

    for (let i = 0; i < colors.length; i++) {
        const color = colors[i];
        const {l, c, h} = lch(color);
        const center = assignments[i];
        sums[center].l += l * color.frequency;
        sums[center].c += c * color.frequency;
        sums[center].h = sums[center].h ?? 0;
        sums[center].h += (h ?? 0) * color.frequency;
        sums[center].frequency += color.frequency;
    }

    return sums.map(sum => {

        // prevent division by zero
        if (sum.frequency === 0) {
            return {
                mode: "lch",
                l: 50,
                c: 0,
                h: 0,
                weight: 0,
                frequency: 1
            };
        }

        const color: Color = {
            mode: "lch",
            l: Math.round(sum.l / sum.frequency),
            c: Math.round(sum.c / sum.frequency),
            h: Math.round((sum.h ?? 0) / sum.frequency)
        };

        return {
            ...color,
            weight: getColorWeight(color),
            frequency: 1
        };
    });
};

const calculateCenterDifference = (
    centers: Array<ColorWithStatistics>,
    newCenters: Array<ColorWithStatistics>
): number => {

    let difference = 0;

    for (let i = 0; i < centers.length; i++) {
        difference += getColorDistance(centers[i], newCenters[i]);
    }

    return difference;
};